import { useState, useEffect, useCallback } from "react";

interface LinkSocialParams {
  provider: string;
  callbackURL: string;
}

interface LinkSocialResult {
  error?: {
    code?: string;
    message?: string;
  } | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LinkSocialFunction = (params: LinkSocialParams) => Promise<any>;

export interface UseLinkLineOptions {
  linkSocial: LinkSocialFunction;
  searchParams: URLSearchParams;
  setSearchParams: (params: Record<string, string>, options?: { replace?: boolean }) => void;
  onSuccess?: () => void;
  t: (key: string, fallback: string) => string;
}

export interface UseLinkLineReturn {
  isLinkingLine: boolean;
  lineError: string | null;
  handleLinkLine: () => Promise<void>;
}

/**
 * LINE アカウント連携のための共通フック
 *
 * @example
 * const { isLinkingLine, lineError, handleLinkLine } = useLinkLine({
 *   linkSocial,
 *   searchParams,
 *   setSearchParams,
 *   onSuccess: () => toast.success(t("settings.lineLinkSuccess", "LINEアカウントを連携しました")),
 *   t,
 * });
 */
export function useLinkLine(options: UseLinkLineOptions): UseLinkLineReturn {
  const { linkSocial, searchParams, setSearchParams, onSuccess, t } = options;

  const [isLinkingLine, setIsLinkingLine] = useState(false);
  const [lineError, setLineError] = useState<string | null>(null);

  // LINE連携成功時のコールバック
  useEffect(() => {
    if (searchParams.get("linked") === "line") {
      onSuccess?.();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, onSuccess]);

  const handleLinkLine = useCallback(async () => {
    setIsLinkingLine(true);
    setLineError(null);
    try {
      const result = (await linkSocial({
        provider: "line",
        callbackURL: `${window.location.origin}/settings?linked=line`,
      })) as LinkSocialResult | undefined;
      if (result?.error) {
        const errorCode = result.error.code;
        if (errorCode === "ACCOUNT_ALREADY_LINKED" || errorCode === "USER_ALREADY_EXISTS") {
          setLineError(
            t("settings.lineAlreadyLinked", "このLINEアカウントは別のユーザーに連携されています")
          );
        } else if (errorCode === "SOCIAL_ACCOUNT_ALREADY_LINKED") {
          setLineError(t("settings.lineAlreadyConnected", "既にLINEアカウントが連携されています"));
        } else {
          setLineError(t("settings.lineLinkFailed", "LINE連携に失敗しました"));
        }
        setIsLinkingLine(false);
      }
    } catch {
      setLineError(t("settings.lineLinkFailed", "LINE連携に失敗しました"));
      setIsLinkingLine(false);
    }
  }, [linkSocial, t]);

  return {
    isLinkingLine,
    lineError,
    handleLinkLine,
  };
}
